import { gql, useQuery } from "@apollo/client";

import { Card, CardHeader, IconButton, CardContent } from "@material-ui/core";
import { Delete } from "@material-ui/icons";

import { getCampaign_campaign } from "src/graphql/generated/getCampaign";
import {
  CampaignStatus,
  JobStatus,
  JobType
} from "src/graphql/generated/globalTypes";
import {
  vanIntegrationWithSavedList,
  vanIntegrationWithSavedListVariables
} from "src/graphql/generated/vanIntegrationWithSavedList";

import DataMapping from "./DataMapping";
import JobResult from "./JobResult";

type JobResult = {
  nContacts: number;
  nInvalid: number;
  nDuplicates: number;
  nNeedingClicks: number;
  nOptOuts: number;
};

const GET_CAMPAIGN_WITH_VAN_SAVED_LIST = gql`
  query vanIntegrationWithSavedList($campaignId: ID!, $listId: Int!) {
    campaign(id: $campaignId) {
      vanIntegration {
        savedList(savedListId: $listId) {
          name
        }
      }
    }
  }
`;

export default function JobComplete({
  campaign,
  onClearFile,
  dataSourceFieldLabel
}: {
  campaign: getCampaign_campaign;
  onClearFile: () => void;
  dataSourceFieldLabel: string;
}): JSX.Element {
  function renderError() {
    return <p>Import failed: {campaign.contactImportJob!.errorMessage}</p>;
  }

  function renderSuccess() {
    const result = JSON.parse(campaign.contactImportJob!.result!);
    return (
      <>
        <JobResult result={result} />
        <DataMapping
          campaign={campaign}
          dataSourceFieldNames={result.importedFieldNames}
          dataSourceFieldLabel={dataSourceFieldLabel}
        />
      </>
    );
  }

  const listId: number =
    campaign.contactImportJob!.type === JobType.CAMPAIGN_VAN_SAVED_LIST_IMPORT
      ? JSON.parse(campaign.contactImportJob!.args!).savedListId
      : -1;

  const {
    data: campaignWithVanSavedList,
    loading: campaignWithVanSavedListLoading
  } = useQuery<
    vanIntegrationWithSavedList,
    vanIntegrationWithSavedListVariables
  >(GET_CAMPAIGN_WITH_VAN_SAVED_LIST, {
    variables: { campaignId: campaign.id, listId },
    skip:
      campaign.contactImportJob!.type !== JobType.CAMPAIGN_VAN_SAVED_LIST_IMPORT
  });

  let title = "Imported Contacts";
  if (campaign.contactImportJob!.type === JobType.CAMPAIGN_CONTACTS_UPLOAD) {
    title = campaign.contactsUpload!.fileName;
  } else if (
    campaign.contactImportJob!.type === JobType.CAMPAIGN_VAN_SAVED_LIST_IMPORT
  ) {
    if (campaignWithVanSavedListLoading) {
      title = "Loading list information from NGP VAN";
    } else if (campaignWithVanSavedList) {
      title =
        campaignWithVanSavedList.campaign.vanIntegration?.savedList.name ??
        "Error fetching list information";
    }
  }

  return (
    <Card>
      <CardHeader
        action={
          campaign.status === CampaignStatus.DRAFT ? (
            <IconButton onClick={onClearFile}>
              <Delete />
            </IconButton>
          ) : null
        }
        title={title}
      />
      <CardContent>
        {campaign.contactImportJob?.status === JobStatus.FAILED
          ? renderError()
          : renderSuccess()}
      </CardContent>
    </Card>
  );
}
